// Partner-Portal-Pyramid (2026-06-24) — read-only downline pyramid for
// the partner's own portal. Mirrors the admin-side DownlinePyramid on
// UserProfilePage.jsx but strips the edit controls — partners shouldn't
// be able to change their introducers' default splits from here (admin
// owns that). Renders the partner at top with their default partner %,
// then each downline introducer / junior broker as a bottom-row node
// with their effective split + active deal count.
//
// Props:
//   partner: {
//     name, email,
//     default_partner_pct, default_introducer_pct, default_jr_broker_pct,
//   }
//   downline: Array<{
//     id, name, email, role,            // role: 'introducer' | 'junior_broker'
//     own_share_default_pct?,           // per-introducer override (else partner default)
//     active_deal_count,
//   }>
//
// SVG is responsive (viewBox + w-full). Empty state shows a friendly
// hint instead of an empty diagram.
import React from "react";

export default function DownlinePyramid({ partner, downline }) {
  const items = downline || [];
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
        <p className="text-sm text-gray-500">
          No downline yet — once Solara onboards introducers under you, they'll appear here.
        </p>
      </div>
    );
  }

  const W = 720;
  const NODE_W = 150;
  const NODE_H = 56;
  const TOP_Y = 14;
  const BOT_Y = 160;
  const cols = items.length;
  const xFor = (i) => {
    if (cols === 1) return (W - NODE_W) / 2;
    const span = W - NODE_W;
    return (span / (cols - 1)) * i;
  };
  const partnerX = (W - NODE_W) / 2;
  const partnerCenterX = partnerX + NODE_W / 2;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-[#021d49]">Your Downline</h2>
        <p className="text-xs text-gray-500">
          {items.length} {items.length === 1 ? "person" : "people"}
        </p>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} 240`} className="w-full max-w-3xl mx-auto" data-testid="partner-portal-pyramid">
          {/* Connector lines from partner down to each downline node */}
          {items.map((d, i) => {
            const cx = xFor(i) + NODE_W / 2;
            return (
              <line
                key={`line-${d.id}`}
                x1={partnerCenterX}
                y1={TOP_Y + NODE_H}
                x2={cx}
                y2={BOT_Y}
                stroke="#cbd5e1"
                strokeWidth="1.5"
              />
            );
          })}
          {/* Partner node */}
          <g>
            <rect x={partnerX} y={TOP_Y} width={NODE_W} height={NODE_H} rx="6" fill="#fef3c7" stroke="#fcd34d" />
            <text x={partnerCenterX} y={TOP_Y + 18} textAnchor="middle" fontSize="11" fontWeight="600" fill="#92400e">
              PARTNER · {partner?.default_partner_pct ?? "—"}%
            </text>
            <text x={partnerCenterX} y={TOP_Y + 36} textAnchor="middle" fontSize="13" fontWeight="600" fill="#021d49">
              {(partner?.name || partner?.email || "You").slice(0, 22)}
            </text>
          </g>
          {/* Downline nodes */}
          {items.map((d, i) => {
            const x = xFor(i);
            const cx = x + NODE_W / 2;
            const isJr = (d.role || "").toLowerCase() === "junior_broker";
            // Per-introducer own_share override beats the partner-level default.
            const effectivePct =
              d.own_share_default_pct ??
              (isJr ? partner?.default_jr_broker_pct : partner?.default_introducer_pct);
            const fillBg = isJr ? "#dbeafe" : "#ede9fe";
            const fillStroke = isJr ? "#93c5fd" : "#c4b5fd";
            const labelColor = isJr ? "#1e40af" : "#5b21b6";
            const roleLabel = isJr ? "JR BROKER" : "INTRODUCER";
            return (
              <g key={`node-${d.id}`}>
                <rect x={x} y={BOT_Y} width={NODE_W} height={NODE_H} rx="6" fill={fillBg} stroke={fillStroke} />
                <text x={cx} y={BOT_Y + 18} textAnchor="middle" fontSize="11" fontWeight="600" fill={labelColor}>
                  {roleLabel} · {effectivePct ?? "—"}%
                </text>
                <text x={cx} y={BOT_Y + 36} textAnchor="middle" fontSize="13" fontWeight="600" fill="#021d49">
                  {(d.name || d.email || "").slice(0, 22)}
                </text>
                <text x={cx} y={BOT_Y + 50} textAnchor="middle" fontSize="10" fill="#64748b">
                  {d.active_deal_count ?? 0} active
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
